import { JACKPOT_FLAG, resolveJackpotFlag } from './resolveJackpotFlag';
import type { JackpotLaunchBlock } from './types';

/**
 * Decides whether a launch response's `jackpot` block is forwarded into the game-ui iframe
 * (KRA-303 FE spec §6.2, hop 3 of the delivery chain).
 *
 * The host renders nothing from it — game-ui draws the in-slot banner (§B0). All this owns is the
 * gate, and it is deliberately the ONLY logic between player-backend and the iframe: the block is
 * forwarded verbatim, because reshaping it here would fork the contract game-ui is built against.
 *
 * ## The flag
 * Resolved through {@link resolveJackpotFlag}, not `resolveFlag`, so the
 * `debug:crown_jackpot_splash_feature` override applies to the banner exactly as it does to the lobby
 * widgets. Without that, a dev with the override on would see the widget but no banner, and the two
 * would silently disagree about whether the feature is on.
 *
 * player-backend gates on the same flag and would not have sent a block, so this is the client-side
 * half of one decision rather than a second one — and it is what stops a launch response that was
 * composed before a kill switch was thrown from still painting a banner.
 *
 * ## The shape guard
 * `jackpotId` and `levels` are checked because the banner cannot work without them: `OPT IN` posts
 * participation against the id, and the popup `More Info` opens renders the levels. A block missing
 * either is a backend older or newer than this client, and dropping it costs a banner — whereas
 * forwarding it would put a broken surface over a live game.
 *
 * `topLevel: null` is NOT a failure and is passed through: it is what a group with no levels
 * carrying a figure looks like, and the banner renders without an amount rather than showing `0`.
 */
export function jackpotLaunchBlockForGameUi(block: JackpotLaunchBlock | undefined): JackpotLaunchBlock | undefined {
  if (!block || !block.jackpotId || !Array.isArray(block.levels)) {
    return undefined;
  }

  return resolveJackpotFlag(JACKPOT_FLAG) ? block : undefined;
}
